import { apiUrl } from "./api";
import { getLocale } from "./i18n";

/**
 * Ships client-side failures to our own server.
 *
 * Inside Discord's iframe there is no console anyone will look at, so a crash
 * is a black rectangle and nothing else. This is the only path by which a
 * failure over there reaches the box it is served from.
 *
 * Everything here is best-effort and silent: a reporter that throws, or that
 * blocks, would make a bad situation worse.
 */

interface Details {
  where: string;
  message: string;
  stack?: string;
  context?: string;
}

/** Filled in once the session exists; reports before that are still useful. */
let sessionId: string | null = null;
let roomId: string | null = null;
let platform = "";
let mapId = "";

export function setReportContext(next: {
  session?: string;
  room?: string;
  platform?: string;
  mapId?: string;
}): void {
  if (next.session !== undefined) sessionId = next.session;
  if (next.room !== undefined) roomId = next.room;
  if (next.platform !== undefined) platform = next.platform;
  if (next.mapId !== undefined) mapId = next.mapId;
}

/** Repeats are common (React retries a failed render); one line each is plenty. */
const seen = new Set<string>();

/**
 * Failures that are not ours.
 *
 * Browser extensions and the host page run their own scripts in this frame and
 * throw their own errors. Reporting them buries the ones we can act on, and
 * "Script error." is what a cross-origin script gives you: no file, no line,
 * nothing to fix.
 */
function isForeign(details: Details): boolean {
  if (details.message === "Script error.") return true;
  // Ours are in the bundle; anything from the page itself is injected.
  const stack = details.stack ?? "";
  return /\/(?!assets\/)[^/]*:\d+$/.test(details.context ?? "") && !stack.includes("/assets/");
}

export function report(details: Details, kind: "error" | "boot" = "error"): void {
  if (kind === "error" && isForeign(details)) return;
  const fingerprint = `${details.where}:${details.message}`;
  if (seen.has(fingerprint)) return;
  seen.add(fingerprint);

  const body = {
    session: sessionId,
    room: roomId,
    kind,
    where: details.where,
    message: details.message,
    stack: details.stack ?? "",
    context: details.context ?? "",
    locale: getLocale(),
    platform,
    mapId,
    build: BUILD_ID,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };

  try {
    // keepalive so the report survives the page being torn down around it.
    void fetch(apiUrl("/api/client-log"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Nothing sensible to do if even queuing the request throws.
  }
}

/** Stamped in at build time so a report names the code it came from. */
export const BUILD_ID: string =
  (import.meta.env?.VITE_BUILD_ID as string | undefined) ?? "dev";

/**
 * Turns anything a promise can reject with into something a log can carry.
 *
 * Exported because the bootstrap catch needs it too: the Discord SDK rejects
 * with a bare `{code, message}` object rather than an Error, and `String()` on
 * that is "[object Object]" -- which is what a whole day of failed launches
 * looked like in the log before this was shared.
 */
export function describe(value: unknown): { message: string; stack: string } {
  if (value instanceof Error) {
    return { message: `${value.name}: ${value.message}`, stack: value.stack ?? "" };
  }
  // `String(someObject)` is "[object Object]", which tells nobody anything.
  if (value !== null && typeof value === "object") {
    try {
      return { message: JSON.stringify(value).slice(0, 400), stack: "" };
    } catch {
      return { message: Object.prototype.toString.call(value), stack: "" };
    }
  }
  return { message: String(value), stack: "" };
}

/** Catches what escapes React: async failures, image loads, everything else. */
export function installGlobalHandlers(): void {
  window.addEventListener("error", (event) => {
    const { message, stack } = describe(event.error ?? event.message);
    report({
      where: "window.error",
      message,
      stack,
      context: `${event.filename ?? ""}:${event.lineno ?? 0}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const { message, stack } = describe(event.reason);
    report({ where: "unhandledrejection", message, stack });
  });
}
